import { COMPANY } from '../../config/company';
import { PAGE_DESCRIPTION } from '../../config-global';
import type { ProductStatus } from '../../data/products';
import { PATH_PAGE } from '../../routes/paths';

export type CompanyField = keyof typeof COMPANY;

export type Accented = Array<string | { i: string }>;

export type Rich = Array<
  string | { b: string } | { i: string } | { field: CompanyField } | { label: string; href: string }
>;

export type Line = string | Rich;

export type PageHead = { title: string; description: string; url: string };

export type LegalSection = { id: string; title: string; body: Rich };

export type LegalDoc = {
  head: PageHead;
  title: string;
  summary: string[];
  sections: LegalSection[];
};

const SITE = 'https://amiroamir.com';

export const HEAD: PageHead = {
  title: 'AmiroAmir — a start-up building consumer web products',
  description: PAGE_DESCRIPTION,
  url: `${SITE}/`,
};

export const SHARE_IMAGE = {
  src: `${SITE}/og.png`,
  width: 1200,
  height: 630,
  alt: 'AmiroAmir. Small products, properly made.',
};

export const ANCHOR = {
  main: 'main',
  top: 'top',
  products: 'products',
  approach: 'approach',
  closing: 'closing',
  product: (id: string) => `product-${id}`,
};

export const HOME_LINK = {
  products: `${PATH_PAGE.home}#${ANCHOR.products}`,
  approach: `${PATH_PAGE.home}#${ANCHOR.approach}`,
};

export const LEGAL_LINKS = [
  { label: 'Privacy', href: PATH_PAGE.privacy },
  { label: 'Terms', href: PATH_PAGE.terms },
];

export const SKIP_LINK = 'Skip to content';

export const WORDMARK = {
  left: 'AMIR',
  o: 'o',
  right: 'AMIR',
  homeLabel: 'AmiroAmir, home',
};

export const ordinal = (n: number) => String(n).padStart(2, '0');

export const productNumber = (index: number) => `No. ${ordinal(index)}`;

export const productLinkAriaLabel = (name: string, host: string) => `${name}, at ${host}`;

export const copyright = (year: number) => `© ${year} ${COMPANY.legalName}.`;

export const placeholder = (field: CompanyField) => `[to be confirmed: ${field}]`;

export const STATUS_NAME: Record<ProductStatus, string> = { beta: 'Beta' };

export const NEXT_PRODUCT = {
  status: 'In the works',
  title: 'Next.',
  line: 'Something new is taking shape. Nothing to announce yet.',
};

export const MENU = {
  id: 'site-menu',
  label: 'Menu',
  open: 'Menu',
  close: 'Close',
  primary: [
    { label: 'Products', href: HOME_LINK.products, section: ANCHOR.products },
    { label: 'Approach', href: HOME_LINK.approach, section: ANCHOR.approach },
    { label: 'Contact', href: PATH_PAGE.contact },
  ],
  productsLabel: 'Products',
  legalLabel: 'Legal',
  legal: LEGAL_LINKS,
};

export const HERO = {
  titleId: 'hero-title',
  srPrefix: 'AmiroAmir. ',
  lines: ['Small products,', [{ i: 'properly' }, ' made.']] as [Line, Line],
  lead: ['AmiroAmir is a small start-up that builds ', { b: 'consumer web products' }, '.'] as Rich,
  primary: { label: 'See our products', href: HOME_LINK.products },
  secondary: { label: 'Our approach', href: HOME_LINK.approach },
};

export const PRODUCTS_SECTION = {
  titleId: 'products-title',
  eyebrow: 'Products',
  lines: ['Our products.', [{ i: 'Made with care.' }]] as [Line, Line],
};

export const APPROACH = {
  titleId: 'approach-title',
  eyebrow: 'Approach',
  statement: [
    'We’d rather make ',
    { i: 'one thing well' },
    ' than ten things in a hurry.',
  ] as Accented,
  principles: [
    {
      title: 'Useful first.',
      body: 'If it doesn’t make a real job easier, it doesn’t make the cut.',
    },
    {
      title: 'Sweat the small stuff.',
      body: 'We care about the details you notice, and the ones you never will.',
    },
    { title: 'Say it plainly.', body: 'We say what a product does, and nothing it doesn’t.' },
  ],
};

export const CLOSING = {
  titleId: 'closing-title',
  lines: ['Everything starts small.', ['Like the ', { i: 'o' }, ' in our name.']] as [Line, Line],
  productsLabel: 'Our products',
};

export const FOOTER = {
  navLabel: 'Footer',
  band: {
    lines: [
      ['Building something ', { i: 'small' }, '\u2009\u2060?'],
      'Come say hello.',
    ] as [Line, Line],
    cta: { label: 'Contact us', href: PATH_PAGE.contact },
  },
  headings: { products: 'Products', company: 'Company', legal: 'Legal', status: 'Status' },
  company: [
    { label: 'Approach', href: HOME_LINK.approach },
    { label: 'Contact', href: PATH_PAGE.contact },
  ],
  legal: LEGAL_LINKS,
  registration: [
    'Registered in ',
    { field: 'jurisdiction' },
    ', company no. ',
    { field: 'companyNumber' },
    '.',
  ] as Rich,
  signOff: 'Made slowly, on purpose.',
  backToTop: 'Back to top',
};

export const LEGAL = {
  eyebrow: 'Legal',
  updated: (date: string) => `Last updated ${date}`,
  summaryLabel: 'In short',
  tocLabel: 'On this page',
  sectionLinkLabel: 'Link to section',
};

export const PRIVACY: LegalDoc = {
  head: {
    title: 'Privacy — AmiroAmir',
    description:
      'How amiroamir.com handles personal data. There are no analytics, advertising or tracking cookies on this site.',
    url: `${SITE}${PATH_PAGE.privacy}`,
  },
  title: 'Privacy',
  summary: [
    'This website doesn’t use analytics, advertising or tracking cookies, and saves nothing in your browser.',
    'We don’t ask you for personal information here. If you email us, we use what you send to reply.',
    'Cloudflare, which delivers the site, receives your IP address. Nothing on these pages is loaded from any other service.',
    'TondTond is a separate service with its own terms.',
  ],
  sections: [
    {
      id: 'who-we-are',
      title: 'Who we are',
      body: [
        'This website, amiroamir.com, is run by ',
        { field: 'legalName' },
        ', a company registered in ',
        { field: 'jurisdiction' },
        ' (company number ',
        { field: 'companyNumber' },
        ') with its registered office at ',
        { field: 'registeredOffice' },
        '. We are the controller of the limited personal data described here. You can reach us at ',
        { field: 'email' },
        '.',
      ],
    },
    {
      id: 'what-we-collect',
      title: 'What this website collects',
      body: [
        'We don’t ask for anything that identifies you directly, and there are no forms, accounts, analytics, advertising or tracking cookies on this site. Like any website, it can’t be delivered without your IP address and basic browser information, which our hosting provider receives, as described below. If you choose to contact us, we also have what you send.',
      ],
    },
    {
      id: 'browser-storage',
      title: 'Information stored in your browser',
      body: [
        'Nothing. Our pages don’t save cookies, local storage or any other data in your browser.',
      ],
    },
    {
      id: 'hosting',
      title: 'Hosting',
      body: [
        'The site is delivered through ',
        { field: 'hostingProvider' },
        ', which processes technical information such as your IP address and browser type to deliver it and protect it from attacks, and may do so outside the UK. We rely on our legitimate interest in running a secure website. Cloudflare keeps this information, and protects any transfer outside the UK, as described in ',
        { label: 'Cloudflare’s privacy policy', href: 'https://www.cloudflare.com/privacypolicy/' },
        '.',
      ],
    },
    {
      id: 'contacting-us',
      title: 'If you contact us',
      body: [
        'If you email us, we use your name, email address and message only to reply to you and to follow up. We rely on our legitimate interest in answering you. Your email is delivered to and stored by our email provider, which may process it outside the UK. We keep your messages for as long as we need them to deal with your enquiry and any follow-up, then delete them. We don’t sell them or use them for marketing.',
      ],
    },
    {
      id: 'our-products',
      title: 'Our products',
      body: [
        'TondTond is a separate service with its own terms at ',
        { label: 'tondtond.com/terms', href: 'https://tondtond.com/terms' },
        '. This notice covers amiroamir.com only.',
      ],
    },
    {
      id: 'your-rights',
      title: 'Your rights',
      body: [
        'You can ask to access, correct or delete the personal data we hold about you, or to restrict how we use it. Email ',
        { field: 'email' },
        ' and we’ll respond within one month. ',
        { b: 'Your right to object:' },
        ' because we rely on legitimate interests, you can object to our use of your data at any time, and we’ll stop unless we have compelling grounds to continue. If you’re unhappy with how we handle your data, you can complain to the UK Information Commissioner’s Office at ',
        { label: 'ico.org.uk', href: 'https://ico.org.uk/make-a-complaint/' },
        ', or to the data protection authority where you live.',
      ],
    },
    {
      id: 'changes',
      title: 'Changes',
      body: ['If we change this notice, we’ll update the date at the top of this page.'],
    },
  ],
};

export const TERMS: LegalDoc = {
  head: {
    title: 'Terms — AmiroAmir',
    description: 'The terms that cover your use of amiroamir.com.',
    url: `${SITE}${PATH_PAGE.terms}`,
  },
  title: 'Terms',
  summary: [
    'These terms cover your use of amiroamir.com.',
    'Our products, like TondTond, have their own terms.',
    'The content here is ours; please don’t copy it without permission.',
  ],
  sections: [
    {
      id: 'about-these-terms',
      title: 'About these terms',
      body: [
        'By using amiroamir.com you agree to these terms. The site is run by ',
        { field: 'legalName' },
        ', a company registered in ',
        { field: 'jurisdiction' },
        ' (company number ',
        { field: 'companyNumber' },
        ') with its registered office at ',
        { field: 'registeredOffice' },
        '.',
      ],
    },
    {
      id: 'using-this-website',
      title: 'Using this website',
      body: [
        'You may browse and share links to this site for personal and non-commercial purposes. Please don’t misuse it, for example by trying to disrupt it, access it without permission or copy it at scale.',
      ],
    },
    {
      id: 'our-products',
      title: 'Our products',
      body: [
        'Each AmiroAmir product has its own terms. TondTond’s are at ',
        { label: 'tondtond.com/terms', href: 'https://tondtond.com/terms' },
        '. If they conflict with these terms, the product’s terms apply to that product.',
      ],
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual property',
      body: [
        'The AmiroAmir, TondTond and Worldstage names, logos, and the design and content of this site belong to ',
        { field: 'legalName' },
        '. You may not use them without our written permission.',
      ],
    },
    {
      id: 'links-to-other-sites',
      title: 'Links to other sites',
      body: ['We link to other websites. We aren’t responsible for their content or practices.'],
    },
    {
      id: 'no-guarantees',
      title: 'No guarantees',
      body: [
        'We work to keep this site accurate and available, but we provide it as it is, without guarantees that it will always be available, complete or error-free.',
      ],
    },
    {
      id: 'liability',
      title: 'Liability',
      body: [
        'We’re responsible for loss you suffer that is a foreseeable result of our breaking these terms or failing to use reasonable care. We aren’t responsible for loss that isn’t foreseeable. The site is for personal use, so we aren’t responsible for business losses such as lost profit or lost business opportunities. We don’t exclude or limit our liability for death or personal injury caused by our negligence, for fraud or fraudulent misrepresentation, or for anything else the law doesn’t allow us to exclude.',
      ],
    },
    {
      id: 'changes',
      title: 'Changes to these terms',
      body: ['We may update these terms. The date at the top shows when they last changed.'],
    },
    {
      id: 'governing-law',
      title: 'Governing law',
      body: [
        'These terms are governed by the law of ',
        { field: 'jurisdiction' },
        ', and you can bring legal proceedings about them in the courts of ',
        { field: 'jurisdiction' },
        '. If you live in Scotland or Northern Ireland, you can also bring proceedings in your local courts. Nothing in these terms affects your rights under the consumer law of the country where you live.',
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      body: ['Questions about these terms: ', { field: 'email' }, '.'],
    },
  ],
};

export const CONTACT = {
  head: {
    title: 'Contact — AmiroAmir',
    description: 'How to reach AmiroAmir, and where to get help with TondTond.',
    url: `${SITE}${PATH_PAGE.contact}`,
  },
  titleId: 'contact-title',
  eyebrow: 'Contact',
  title: ['Say ', { i: 'hello.' }] as Rich,
  lead: 'We’re a small team, so every message is read by someone who builds the products.',
  general: {
    title: 'General',
    body: 'For anything about AmiroAmir: questions, ideas or partnerships.',
    copy: 'Copy address',
    copied: 'Copied',
    selected: 'Address selected. Press Ctrl+C or ⌘C to copy.',
  },
  support: {
    title: 'TondTond support',
    body: 'Using TondTond and need a hand? Its team can help.',
    link: { label: 'Contact TondTond', href: 'https://tondtond.com/contact' },
  },
  details: [
    { field: 'legalName' },
    ' · Registered in ',
    { field: 'jurisdiction' },
    ', company no. ',
    { field: 'companyNumber' },
    ' · Registered office: ',
    { field: 'registeredOffice' },
  ] as Rich,
};
